/**
 * DmlColumnPicker — the column chooser for the columns a DML statement writes:
 * a SET assignment's column (add + change) and an INSERT column-list entry.
 * Lists the written table's columns from the schema snapshot (type + PK/FK
 * badges); columns already in the list are check-marked and not pickable, so
 * the picker can't produce a duplicate. Typing a name the table doesn't list
 * offers "Use <name>" — a column the snapshot hasn't loaded, or a table the
 * schema doesn't know at all, is still one keystroke away.
 *
 * `onPick` hands back the catalog column (or null for a typed name) plus the
 * SQL text to write: catalog names delimited per engine when needed, prefixed
 * with `qualifier` when the list writes qualified targets; a typed name is
 * kept verbatim (it only gains the qualifier when it has none of its own).
 */

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { Column } from '../../../../sql-resolver/types/schema';
import { vqeCopy } from '../../ux/vqeCopy';
import { ColumnBadges } from '../common/ColumnBadges';
import { CommandPickerPopover, type CommandPickerItem } from '../common/CommandPickerPopover';
import { isSameColumn, isSameRef, splitColumnRef } from '../../../../sql-resolver/scope/columnRefs';
import { columnSqlText, findColumn, type WrittenTableColumns } from './assignmentColumns';

export interface DmlColumnPick {
  readonly column: Column | null;
  readonly text: string;
}

export interface DmlColumnPickerProps {
  readonly testId: string;
  readonly table: WrittenTableColumns;
  /** Columns already written by the list — check-marked, not pickable. */
  readonly takenRefs: readonly string[];
  /** Qualifier for the SQL text (`t` → `t.col`), or null for bare names. */
  readonly qualifier: string | null;
  readonly onPick: (pick: DmlColumnPick) => void;
  /** Analytics tag: which list the pick lands in (adding = the ghost trigger, swapping = inline). */
  readonly usage: 'set' | 'insert';
  readonly triggerLabel: string;
  /** `'inline'`: the column name is the trigger (swap a column) — its accessible name says so. */
  readonly triggerVariant?: 'ghost' | 'inline';
  readonly triggerClassName?: string;
  readonly defaultOpen?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

export function DmlColumnPicker({
  testId,
  table,
  takenRefs,
  qualifier,
  onPick,
  usage,
  triggerLabel,
  triggerVariant = 'ghost',
  triggerClassName,
  defaultOpen,
  onOpenChange,
}: DmlColumnPickerProps) {
  const { track } = useAnalytics();
  const { engine } = table;
  const columns = table.columns ?? [];
  const isTaken = (name: string): boolean => takenRefs.some((ref) => isSameColumn(ref, name, engine));
  // Folded name: the id the INSERT add-column picker has always used — made
  // unique for PostgreSQL / Oracle case-distinct columns ("Name" and name).
  const keys = new Map<string, Column>();
  const items: CommandPickerItem[] = columns.map((column, index) => {
    const taken = isTaken(column.qualified.name);
    const folded = column.qualified.foldedName;
    const key = keys.has(folded) ? `${folded}#${index}` : folded;
    keys.set(key, column);
    return {
      key,
      label: column.qualified.name,
      meta: column.formattedType ?? column.dataType,
      badges: <ColumnBadges isPrimaryKey={column.isPrimaryKey} isForeignKey={column.isForeignKey} />,
      checked: taken,
      disabled: taken,
    };
  });

  const pick = (column: Column | null, text: string): void => {
    track('feature:vqe_column_pick', {
      target: usage,
      action: triggerVariant === 'inline' ? 'change' : 'add',
      source: column ? 'schema' : 'custom',
    });
    onPick({ column, text });
  };

  return (
    <CommandPickerPopover
      testId={testId}
      triggerLabel={triggerLabel}
      triggerVariant={triggerVariant}
      triggerClassName={triggerClassName}
      hint={triggerVariant === 'inline' ? `${triggerLabel} · ${vqeCopy.dmlColumns.changeColumn}` : undefined}
      disabled={false}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      closeOnSelect
      groups={items.length > 0 ? [{ heading: table.tableName ?? undefined, items }] : []}
      searchPlaceholder={vqeCopy.dmlColumns.searchPlaceholder}
      emptyMessage={vqeCopy.dmlColumns.columnsUnknown}
      onSelect={(key) => {
        const column = keys.get(key);
        if (column) pick(column, columnSqlText(column, engine, qualifier));
      }}
      customEntry={{
        meta: table.columns ? vqeCopy.dmlColumns.notInTable : undefined,
        // No "Use <name>" for a column the list already has, however it is
        // spelled (`[StatusCd]`, `t.statuscd`) — a second assignment to it.
        accept: (typed) => {
          const column = findColumn(table, typed);
          return !takenRefs.some((ref) => isSameRef(ref, typed, engine)) && !(column && isTaken(column.qualified.name));
        },
        onPick: (typed) => {
          // A typed name the table does list after all (case differs) is the catalog column.
          const column = findColumn(table, typed);
          if (column) {
            pick(column, columnSqlText(column, engine, qualifier));
            return;
          }
          pick(null, qualifier && !splitColumnRef(typed).qualifier ? `${qualifier}.${typed}` : typed);
        },
      }}
    />
  );
}
